import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import moment from 'moment';
import PropTypes from 'prop-types';

import {HISTORY_SHOW_TYPE, CYCLE_LOG_PROFILE_MODE} from '@/constants/enums';
import {consoleLog} from '@/libs/consoleHelper';

import {
  useCycleHistoryContext,
  useCycleHistoryFilterContext,
  useCycleHistoryProfileModeContext,
} from './CycleHistoryContext';

const CycleHistoryDetailContext = createContext(null);
const CycleHistoryChangeDetailContext = createContext(null);

export function CycleHistoryDetailContextProvider({children}) {
  const [cycleLog, setCycleLog] = useState(null);
  const [selectedCycleHistoryListMode, setSelectedCycleHistoryListMode] = useState({});
  const {cycleHistoryList} = useCycleHistoryContext();
  const {profileMode} = useCycleHistoryProfileModeContext();
  const {showType, searchFilter, searchFilterBegin, searchFilterEnd} = useCycleHistoryFilterContext();

  const isShowDetail = useMemo(() => cycleLog !== null, [cycleLog]);

  const selectedCycleHistoryList = useMemo(() => {
    const logFilter = (log) => {
      const filterResult =
        showType === HISTORY_SHOW_TYPE.DATE
          ? moment(log.Date).isSameOrAfter(searchFilterBegin) && moment(log.Date).isSameOrBefore(searchFilterEnd)
          : true;
      log.Active = filterResult;
      return filterResult;
    };

    switch (profileMode) {
      case CYCLE_LOG_PROFILE_MODE.AUTO_PROFILE:
      case CYCLE_LOG_PROFILE_MODE.MANUAL_PROFILE:
        return selectedCycleHistoryListMode[profileMode]
          ? selectedCycleHistoryListMode[profileMode].filter(logFilter)
          : [];

      case CYCLE_LOG_PROFILE_MODE.ALL: {
        const logAutoProfile = selectedCycleHistoryListMode[CYCLE_LOG_PROFILE_MODE.AUTO_PROFILE]
          ? selectedCycleHistoryListMode[CYCLE_LOG_PROFILE_MODE.AUTO_PROFILE]
          : [];
        const logManualProfile = selectedCycleHistoryListMode[CYCLE_LOG_PROFILE_MODE.MANUAL_PROFILE]
          ? selectedCycleHistoryListMode[CYCLE_LOG_PROFILE_MODE.MANUAL_PROFILE]
          : [];
        return [...logAutoProfile.filter(logFilter), ...logManualProfile.filter(logFilter)];
      }
      default:
        return [];
    }
  }, [selectedCycleHistoryListMode, profileMode, showType, searchFilter, searchFilterBegin, searchFilterEnd]);

  /**
   * sync the refference between selected log with available cyle log
   */
  useEffect(() => {
    const {value} = cycleHistoryList;
    value.forEach((log) => {
      const selectedLog = selectedCycleHistoryList.find((selected) => selected.Id === log.Id);
      if (selectedLog && log !== selectedLog) {
        SetSelectedCycleHistoryList(selectedLog);
      }
    });
    consoleLog('useEffect SetSelectedCycleHistoryListArray', {
      cycleHistoryList,
      selectedCycleHistoryListMode,
      selectedCycleHistoryList,
    });
  }, [cycleHistoryList, selectedCycleHistoryList]);

  const SetSelectedCycleHistoryList = useCallback(
    (selectedLog) => {
      const {Profile: mode} = selectedLog;

      const cycleLogMode = selectedCycleHistoryListMode[mode] || [];
      // Check if the selected log is already in the list
      const logIndex = cycleLogMode.findIndex((log) => log.Id === selectedLog.Id);
      selectedLog.Active = logIndex === -1;

      if (logIndex === -1) {
        cycleLogMode.push(selectedLog);
      } else {
        cycleLogMode.splice(logIndex, 1);
      }

      setSelectedCycleHistoryListMode({
        ...selectedCycleHistoryListMode,
        [mode]: cycleLogMode,
      });

      consoleLog('SetSelectedCycleHistoryList', {
        cycleHistoryList,
        selectedCycleHistoryListMode,
        selectedCycleHistoryList,
      });
    },
    [selectedCycleHistoryListMode],
  );

  const SetSelectedCycleHistoryListArray = useCallback(
    (_selectedLoglist) => {
      let selectedLoglist = _selectedLoglist;
      let tempSelectedCycleHistoryListMode = {};
      const tempSelected = [];
      tempSelected[CYCLE_LOG_PROFILE_MODE.AUTO_PROFILE] = [];
      tempSelected[CYCLE_LOG_PROFILE_MODE.MANUAL_PROFILE] = [];

      const selectedLoglist_ = selectedLoglist.forEach((log) => {
        consoleLog('SetSelectedCycleHistoryListArray1', {
          cycleHistoryList,
          selectedCycleHistoryListMode,
          selectedCycleHistoryList,
          log,
          selectedLoglist,
          tempSelectedCycleHistoryListMode,
        });
        const mode = log.Name.includes(CYCLE_LOG_PROFILE_MODE.AUTO_PROFILE)
          ? CYCLE_LOG_PROFILE_MODE.AUTO_PROFILE
          : CYCLE_LOG_PROFILE_MODE.MANUAL_PROFILE;
        log.Active = true;
        tempSelected[mode].push(log);

        tempSelectedCycleHistoryListMode = {...tempSelectedCycleHistoryListMode, [mode]: tempSelected[mode]};

        consoleLog('SetSelectedCycleHistoryListArray2', {
          cycleHistoryList,
          selectedCycleHistoryListMode,
          selectedCycleHistoryList,
          log,
          selectedLoglist,
          tempSelectedCycleHistoryListMode,
        });
      });

      selectedLoglist = selectedLoglist_;

      setSelectedCycleHistoryListMode(tempSelectedCycleHistoryListMode);
      consoleLog('SetSelectedCycleHistoryListArray', {
        cycleHistoryList,
        selectedCycleHistoryListMode,
        selectedCycleHistoryList,
        selectedLoglist,
        tempSelectedCycleHistoryListMode,
      });
    },
    [selectedCycleHistoryListMode],
  );

  /**
   * @param {Object} value
   */
  const SetCycleLog = useCallback((value) => setCycleLog(value), []);

  const CycleHistoryDetailContextValue = useMemo(
    () => ({
      cycleLog,
      isShowDetail,
      selectedCycleHistoryList,
      SetSelectedCycleHistoryList,
      SetSelectedCycleHistoryListArray,
    }),
    [cycleLog, selectedCycleHistoryList],
  );

  return (
    <CycleHistoryDetailContext.Provider value={CycleHistoryDetailContextValue}>
      <CycleHistoryChangeDetailContext.Provider value={SetCycleLog}>
        {children}
      </CycleHistoryChangeDetailContext.Provider>
    </CycleHistoryDetailContext.Provider>
  );
}

CycleHistoryDetailContextProvider.propTypes = {
  children: PropTypes.any,
};
CycleHistoryDetailContextProvider.defaultProps = {
  children: null,
};

export const useCycleHistoryDetailContext = () => useContext(CycleHistoryDetailContext);
export const useCycleHistoryChangeDetailContext = () => useContext(CycleHistoryChangeDetailContext);
